import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {MoreVertical} from "lucide-react";
import {cn} from "../../utils/cn";

/**
 * Row actions dropdown. Replaces inline icon-button rows.
 *
 * items: [{ label, icon, onClick, danger, hidden, disabled, shortcut, divider }]
 *   - divider: true to render a separator (omit label/onClick)
 */
export default function KebabMenu({items = [], ariaLabel = "Row actions", align = "end", side = "bottom"}) {
  const visible = items.filter((i) => i && !i.hidden);
  if (visible.length === 0) return null;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex items-center justify-center h-8 w-8 rounded-md",
            "text-ink-500 hover:text-ink-700 hover:bg-ink-100",
            "transition-colors duration-fast",
            "focus-visible:outline-none focus-visible:ring focus-visible:ring-offset-1",
            "ui-open:bg-ink-100 ui-open:text-ink-900"
          )}
        >
          <MoreVertical className="h-4 w-4" aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          side={side}
          sideOffset={4}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "z-dropdown min-w-[200px] p-1",
            "bg-white rounded-lg border border-ink-200 shadow-md",
            "data-[state=open]:animate-slide-in-up",
            "data-[state=closed]:animate-fade-out"
          )}
        >
          {visible.map((item, idx) =>
            item.divider ? (
              <DropdownMenu.Separator key={idx} className="h-px bg-ink-100 my-1" />
            ) : (
              <DropdownMenu.Item
                key={idx}
                disabled={item.disabled}
                onSelect={(e) => {
                  e.preventDefault();
                  item.onClick && item.onClick();
                }}
                className={cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer",
                  "text-base text-ink-700 outline-none",
                  "data-[highlighted]:bg-ink-100",
                  "data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed",
                  item.danger && "text-danger-600 data-[highlighted]:bg-danger-50"
                )}
              >
                {item.icon && (
                  <item.icon
                    className={cn("h-4 w-4 shrink-0", item.danger ? "text-danger-500" : "text-ink-500")}
                    aria-hidden="true"
                  />
                )}
                <span className="flex-1 truncate">{item.label}</span>
                {item.shortcut && (
                  <kbd className="ml-2 text-2xs font-mono text-ink-400 border border-ink-200 rounded px-1 py-0.5">
                    {item.shortcut}
                  </kbd>
                )}
              </DropdownMenu.Item>
            )
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
